import { createContext, ReactNode, useContext } from 'react';
import { apiV2 } from '../services/api';

type EntityId = string | number;

export interface PayrollBatchEmployeeBoat {
  id_payroll_batch_employee_trip?: EntityId;
  fk_trip_id: EntityId;
  fk_cost_center_id: EntityId;
}

export interface PayrollBatchEmployee {
  id_payroll_batch_employee: EntityId;
  fk_employee_id: EntityId;
  employee_name?: string;
  fk_payment_type_id: EntityId;
  paymenttype_name?: string;
  value: number;
  is_indirect_cost: boolean;
  boats: PayrollBatchEmployeeBoat[];
}

export interface PayrollBatch {
  id_payroll_batch: EntityId;
  month: number;
  year: number;
  fk_cost_category_id: EntityId;
  cost_category_name?: string;
  expiration_date: string;
  status?: 'DRAFT' | 'CLOSED' | 'EXECUTED';
  canClose?: boolean;
  canReopen?: boolean;
  canUploadPaymentImport?: boolean;
  employees?: PayrollBatchEmployee[];
}

export interface PayrollPaymentImportItem {
  id: EntityId;
  order_id?: EntityId | null;
  employee_id?: EntityId | null;
  employee_name: string;
  cpf?: string | null;
  agency_account?: string | null;
  payment_status?: string | null;
  payment_type?: string | null;
  amount: number;
  matched: boolean;
  divergence?: string | null;
}

export interface PayrollPaymentImport {
  id: EntityId;
  payroll_batch_id?: EntityId | null;
  file_name: string;
  source_bank: string;
  payment_date?: string | null;
  status: 'UPLOADED' | 'PROCESSING' | 'PROCESSED' | 'CONFIRMED' | 'CANCELLED';
  total_amount?: number | null;
  payment_count?: number | null;
  metadata?: Record<string, unknown> | null;
  items: PayrollPaymentImportItem[];
}

export type PayrollBatchInput = {
  month: number;
  year: number;
  fk_cost_category_id: EntityId;
  expiration_date?: string;
};

export type PayrollBatchEmployeeInput = {
  fk_employee_id: EntityId;
  fk_payment_type_id: EntityId;
  value: number;
  is_indirect_cost: boolean;
  boats: PayrollBatchEmployeeBoat[];
};

export interface PayrollProviderFilter {
  page?: number;
  pageSize?: number;
}

export interface PayrollProviderResponse<T = undefined> {
  success: boolean;
  message: string;
  data?: T;
}

export interface PayrollPaginatedResponse<T> {
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  data: T[];
}

interface PayrollProviderProps {
  children: ReactNode;
}

interface PayrollProviderContextData {
  getPayrollBatches: (filter?: PayrollProviderFilter) => Promise<PayrollProviderResponse<PayrollPaginatedResponse<PayrollBatch>>>;
  getPayrollBatch: (batchId: EntityId) => Promise<PayrollProviderResponse<PayrollBatch>>;
  createPayrollBatch: (batch: PayrollBatchInput) => Promise<PayrollProviderResponse<PayrollBatch>>;
  updatePayrollBatch: (batchId: EntityId, batch: PayrollBatchInput) => Promise<PayrollProviderResponse>;
  closePayrollBatch: (batchId: EntityId) => Promise<PayrollProviderResponse>;
  reopenPayrollBatch: (batchId: EntityId) => Promise<PayrollProviderResponse>;
  uploadPayrollPaymentReport: (batchId: EntityId, file: File) => Promise<PayrollProviderResponse<PayrollPaymentImport>>;
  confirmPayrollPaymentImport: (paymentImportId: EntityId) => Promise<PayrollProviderResponse<PayrollPaymentImport>>;
  createPayrollEmployee: (batchId: EntityId, employee: PayrollBatchEmployeeInput) => Promise<PayrollProviderResponse<PayrollBatchEmployee>>;
  updatePayrollEmployee: (batchId: EntityId, employeeId: EntityId, employee: PayrollBatchEmployeeInput) => Promise<PayrollProviderResponse>;
  deletePayrollEmployee: (batchId: EntityId, employeeId: EntityId) => Promise<PayrollProviderResponse>;
}

const PayrollProviderContext = createContext<PayrollProviderContextData>(
  {} as PayrollProviderContextData
);

export const payrollRoutes = {
  batches: '/payroll-batches',
  batch: (batchId: EntityId) => `/payroll-batches/${batchId}`,
  closeBatch: (batchId: EntityId) => `/payroll-batches/${batchId}/close`,
  reopenBatch: (batchId: EntityId) => `/payroll-batches/${batchId}/reopen`,
  paymentImports: '/payment-imports',
  confirmPaymentImport: (paymentImportId: EntityId) => `/payment-imports/${paymentImportId}/confirm`,
  employees: (batchId: EntityId) => `/payroll-batches/${batchId}/employees`,
  employee: (batchId: EntityId, employeeId: EntityId) =>
    `/payroll-batches/${batchId}/employees/${employeeId}`
};

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /prismaclient|unique constraint|duplicate key|aws|s3|storage|bucket|credentials|accessdenied|serviceunavailable/i.test(message) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownApiMessage(message: unknown) {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^payroll batch not found\.?$/i.test(normalizedMessage)) {
    return 'Folha de pagamento não encontrada.';
  }

  if (/^validation failed\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível validar os dados enviados. Revise as informações e tente novamente.';
  }

  if (/^a pdf file is required\.?$/i.test(normalizedMessage)) {
    return 'Selecione um arquivo PDF válido.';
  }

  if (/aws storage is not configured/i.test(normalizedMessage)) {
    return 'O armazenamento de arquivos não está configurado. Entre em contato com o suporte.';
  }

  if (/could not load credentials|credentials.*provider|missing credentials|invalid.*credentials/i.test(normalizedMessage)) {
    return 'Não foi possível acessar o armazenamento de arquivos. Entre em contato com o suporte.';
  }

  if (/accessdenied|access denied|not authorized|forbidden/i.test(normalizedMessage) && /(aws|s3|storage|bucket|object|file)/i.test(normalizedMessage)) {
    return 'Sem permissão para acessar o armazenamento do relatório. Entre em contato com o suporte.';
  }

  if (/nosuchbucket|bucket.*not.*found|specified bucket does not exist/i.test(normalizedMessage)) {
    return 'O local de armazenamento do relatório não foi encontrado. Entre em contato com o suporte.';
  }

  if (/(aws|s3|storage|bucket).*(error|failed|unavailable|timeout)/i.test(normalizedMessage)) {
    return 'Não foi possível salvar o relatório no armazenamento de arquivos. Tente novamente ou entre em contato com o suporte.';
  }

  if (/^the file is not a banco do brasil payment report\.?$/i.test(normalizedMessage)) {
    return 'O arquivo selecionado não parece ser um relatório de pagamento do Banco do Brasil.';
  }

  if (/^payment import not found\.?$/i.test(normalizedMessage)) {
    return 'Importação de pagamento não encontrada.';
  }

  if (/^service batch not found\.?$/i.test(normalizedMessage)) {
    return 'Folha de pagamento não encontrada.';
  }

  if (/^only processed imports can be confirmed\.?$/i.test(normalizedMessage)) {
    return 'Apenas importações processadas podem ser confirmadas.';
  }

  if (/^payment date was not found in the report\.?$/i.test(normalizedMessage)) {
    return 'A data de pagamento não foi encontrada no relatório.';
  }

  if (/payment item .+ is no longer compatible with its order\.?/i.test(normalizedMessage)) {
    return 'Um ou mais itens do relatório não são mais compatíveis com os pedidos relacionados.';
  }

  if (/orders were confirmed, but the audit file could not be moved/i.test(normalizedMessage)) {
    return 'Os pedidos foram confirmados, mas não foi possível mover o arquivo de auditoria.';
  }

  if (/^order not found\.?$/i.test(normalizedMessage)) {
    return 'Um dos pedidos vinculados ao relatório não foi encontrado.';
  }

  if (/^order has already been executed\.?$/i.test(normalizedMessage)) {
    return 'Um dos pedidos vinculados ao relatório já foi executado.';
  }

  if (/^installment orders cannot be paid by payment import\.?$/i.test(normalizedMessage)) {
    return 'Pedidos parcelados não podem ser executados pela importação do relatório de pagamento.';
  }

  if (/unique constraint failed|duplicate key|payroll_batch_company_id_year_month_cost_category_id_key|P2002/i.test(normalizedMessage)) {
    return 'Já existe uma folha de pagamento cadastrada para esta categoria, mês e ano.';
  }

  if (/^the payroll batch has no employees\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível fechar a folha porque ela não possui colaboradores.';
  }

  if (/^only draft payroll batches can be closed\.?$/i.test(normalizedMessage)) {
    return 'Apenas folhas em rascunho podem ser fechadas.';
  }

  if (/^payroll batch closed successfully\.?$/i.test(normalizedMessage)) {
    return 'Folha de pagamento fechada com sucesso.';
  }

  if (/^payroll batch reopened successfully\.?$/i.test(normalizedMessage)) {
    return 'Folha de pagamento reaberta com sucesso.';
  }

  if (/^payroll batch updated successfully\.?$/i.test(normalizedMessage)) {
    return 'Dados da folha de pagamento alterados com sucesso.';
  }

  if (/^only closed and executed payroll batches can be reopened\.?$/i.test(normalizedMessage)) {
    return 'Apenas folhas fechadas ou executadas podem ser reabertas.';
  }

  if (/^one or more generated orders have already been executed\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível reabrir a folha porque um ou mais pedidos gerados já foram executados.';
  }

  if (/^payment reports can only be uploaded for closed payroll batches\.?$/i.test(normalizedMessage)) {
    return 'O relatório de pagamento só pode ser importado para folhas fechadas.';
  }

  if (/^every payroll employee must have at least one trip\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível fechar a folha porque existe colaborador sem barco/viagem.';
  }

  if (/^closed payroll batches cannot be modified\.?$/i.test(normalizedMessage)) {
    return 'Esta folha de pagamento não pode ser alterada neste status.';
  }

  if (/^employee not found\.?$/i.test(normalizedMessage)) {
    return 'Colaborador não encontrado.';
  }

  if (/^payment type not found\.?$/i.test(normalizedMessage)) {
    return 'Tipo de pagamento não encontrado.';
  }

  if (/^trips cannot be repeated\.?$/i.test(normalizedMessage)) {
    return 'Não repita barco/viagem no mesmo colaborador.';
  }

  if (/^cost category must be/i.test(normalizedMessage)) {
    return 'Selecione uma categoria de custo de quinzena válida.';
  }

  return undefined;
}

function toResponse<T>(response: any, successMessage: string, errorMessage: string): PayrollProviderResponse<T> {
  const success = response?.success !== false && response?.status >= 200 && response?.status < 300;
  const apiMessage = response?.data?.message || response?.message;
  const translatedMessage = translateKnownApiMessage(apiMessage);
  const hasDataProperty = response?.data && Object.prototype.hasOwnProperty.call(response.data, 'data');
  const payload = hasDataProperty ? response.data.data : response?.data;

  return {
    success,
    message: success ? successMessage : translatedMessage || (isTechnicalMessage(apiMessage) ? errorMessage : apiMessage),
    data: success ? payload : undefined
  };
}

function removeEmptyFilters(filter: PayrollProviderFilter) {
  return Object.entries(filter).reduce<Record<string, unknown>>((params, [key, value]) => {
    if (value !== "''" && value !== '' && value !== undefined && value !== null) {
      params[key] = value;
    }
    return params;
  }, {});
}

export function PayrollProvider({ children }: PayrollProviderProps) {
  async function getPayrollBatches(filter: PayrollProviderFilter = {}) {
    const response: any = await apiV2.get(payrollRoutes.batches, { params: removeEmptyFilters(filter) });
    const apiMessage = response?.data?.message || response?.message;
    const translatedMessage = translateKnownApiMessage(apiMessage);
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    return {
      success,
      message: success ? 'Folhas consultadas com sucesso!' : translatedMessage || (isTechnicalMessage(apiMessage) ? 'Não foi possível consultar as folhas de pagamento.' : apiMessage),
      data: success ? response?.data : undefined
    };
  }

  async function getPayrollBatch(batchId: EntityId) {
    const response = await apiV2.get(payrollRoutes.batch(batchId));
    return toResponse<PayrollBatch>(response, 'Folha consultada com sucesso!', 'Não foi possível consultar a folha de pagamento.');
  }

  async function createPayrollBatch(batch: PayrollBatchInput) {
    const response = await apiV2.post(payrollRoutes.batches, batch);
    return toResponse<PayrollBatch>(response, 'Folha de pagamento adicionada com sucesso!', 'Não foi possível adicionar a folha de pagamento.');
  }

  async function updatePayrollBatch(batchId: EntityId, batch: PayrollBatchInput) {
    const response = await apiV2.put(payrollRoutes.batch(batchId), batch);
    return toResponse<undefined>(response, 'Dados da folha de pagamento alterados com sucesso!', 'Não foi possível alterar os dados da folha de pagamento.');
  }

  async function closePayrollBatch(batchId: EntityId) {
    const response = await apiV2.post(payrollRoutes.closeBatch(batchId));
    return toResponse<undefined>(response, 'Folha de pagamento fechada com sucesso!', 'Não foi possível fechar a folha de pagamento.');
  }

  async function reopenPayrollBatch(batchId: EntityId) {
    const response = await apiV2.post(payrollRoutes.reopenBatch(batchId));
    return toResponse<undefined>(response, 'Folha de pagamento reaberta com sucesso!', 'Não foi possível reabrir a folha de pagamento.');
  }

  async function uploadPayrollPaymentReport(batchId: EntityId, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('payroll_batch_id', String(batchId));

    const response = await apiV2.post(payrollRoutes.paymentImports, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return toResponse<PayrollPaymentImport>(response, 'Relatório de pagamento importado com sucesso!', 'Não foi possível importar o relatório de pagamento.');
  }

  async function confirmPayrollPaymentImport(paymentImportId: EntityId) {
    const response = await apiV2.post(payrollRoutes.confirmPaymentImport(paymentImportId));
    return toResponse<PayrollPaymentImport>(response, 'Pedidos executados com sucesso!', 'Não foi possível confirmar a execução dos pedidos.');
  }

  async function createPayrollEmployee(batchId: EntityId, employee: PayrollBatchEmployeeInput) {
    const response = await apiV2.post(payrollRoutes.employees(batchId), employee);
    return toResponse<PayrollBatchEmployee>(response, 'Colaborador adicionado à folha com sucesso!', 'Não foi possível adicionar o colaborador à folha.');
  }

  async function updatePayrollEmployee(batchId: EntityId, employeeId: EntityId, employee: PayrollBatchEmployeeInput) {
    const response = await apiV2.put(payrollRoutes.employee(batchId, employeeId), employee);
    return toResponse<undefined>(response, 'Colaborador da folha alterado com sucesso!', 'Não foi possível alterar o colaborador da folha.');
  }

  async function deletePayrollEmployee(batchId: EntityId, employeeId: EntityId) {
    const response = await apiV2.delete(payrollRoutes.employee(batchId, employeeId));
    return toResponse<undefined>(response, 'Colaborador removido da folha com sucesso!', 'Não foi possível remover o colaborador da folha.');
  }

  return (
    <PayrollProviderContext.Provider value={{
      getPayrollBatches,
      getPayrollBatch,
      createPayrollBatch,
      updatePayrollBatch,
      closePayrollBatch,
      reopenPayrollBatch,
      uploadPayrollPaymentReport,
      confirmPayrollPaymentImport,
      createPayrollEmployee,
      updatePayrollEmployee,
      deletePayrollEmployee
    }}>
      {children}
    </PayrollProviderContext.Provider>
  );
}

export function UsePayrollProvider() {
  return useContext(PayrollProviderContext);
}
